import React, { useState } from "react";
import { Header } from "../../../../components";
import { Table, Input, Space, Button, Popconfirm, Modal, Form, Select, message } from "antd";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBlacklistList,
  useUpdateBlacklistEntry,
  useDeleteBlacklistEntry,
} from "../hooks/useFetch";
import { useBlacklistTopics } from "../../../../hooks/useBlacklist";
import { bank as bankList } from "../../../../data/bank";
import { JOB_TYPE } from "../../../../data/constant";

// สิทธิ์ Blacklist — ต้องตรงกับ guard ฝั่ง backend (blacklistModel.canEditEntry / canDeleteEntry)
//
// แก้ไข: Admin/Backoffice (role 1,2) + Head + Manager
//        ไม่รวม Panel(Output) = คนกรอก blacklist (ดูได้อย่างเดียว) และ Auditor
// ลบ   : เฉพาะ Admin ขึ้นไป (role 1,2) เท่านั้น — Head/Manager แก้ได้แต่ลบไม่ได้
const isAdminOrAbove = () => {
  const role = String(sessionStorage.getItem("role"));
  return role === "1" || role === "2";
};

const canEditBlacklist = () => {
  if (isAdminOrAbove()) return true;
  const jobType = Number(sessionStorage.getItem("jobType"));
  return jobType === JOB_TYPE.head || jobType === JOB_TYPE.manager;
};

const canDeleteBlacklist = () => isAdminOrAbove();

function BlacklistPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isFetching } = useBlacklistList({ page, pageSize, search });
  const { data: topics = [] } = useBlacklistTopics();
  const { mutateAsync: updateEntry, isLoading: isUpdating } = useUpdateBlacklistEntry();
  const { mutateAsync: deleteEntry } = useDeleteBlacklistEntry();

  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const canEdit = canEditBlacklist();
  const canDelete = canDeleteBlacklist();

  const rows = data?.data || [];
  const total = data?.total || 0;

  const refresh = () => queryClient.invalidateQueries(["blacklistList"]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      topicId: record.topic_id,
      accountName: record.account_name,
      bank: record.bank,
      bankAccount: record.bank_account,
      note: record.note,
    });
  };

  const handleEditOk = async () => {
    try {
      const values = await form.validateFields();
      await updateEntry({ id: editing.id, ...values });
      await message.destroy();
      message.success("แก้ไขสำเร็จ");
      setEditing(null);
      refresh();
    } catch (error) {
      if (error?.errorFields) return;
      await message.destroy();
      message.error(error?.response?.data?.message || "แก้ไขไม่สำเร็จ");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEntry(id);
      await message.destroy();
      message.success("ลบสำเร็จ");
      refresh();
    } catch (error) {
      await message.destroy();
      message.error(error?.response?.data?.message || "ลบไม่สำเร็จ");
    }
  };

  const columns = [
    { title: "ลำดับ", align: "center", width: 70, render: (_, __, i) => (page - 1) * pageSize + i + 1 },
    { title: "UserId", dataIndex: "userId", key: "userId", align: "center" },
    {
      title: "หัวข้อ",
      dataIndex: "topic_name",
      key: "topic_name",
      render: (t) => <span style={{ color: "#cf1322", fontWeight: 500 }}>{t || "-"}</span>,
    },
    { title: "ชื่อบัญชี", dataIndex: "account_name", key: "account_name", render: (t) => t || "-" },
    { title: "ธนาคาร", dataIndex: "bank", key: "bank", render: (t) => t || "-" },
    { title: "เลขบัญชี", dataIndex: "bank_account", key: "bank_account", render: (t) => t || "-" },
    {
      title: "หมายเหตุ",
      dataIndex: "note",
      key: "note",
      width: 220,
      render: (t) => (t ? <span style={{ whiteSpace: "pre-wrap" }}>{t}</span> : "-"),
    },
    { title: "เพิ่มโดย", dataIndex: "create_by_name", key: "create_by_name", align: "center", render: (t) => t || "-" },
    {
      title: "วันเวลา",
      dataIndex: "create_date",
      key: "create_date",
      align: "center",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-"),
    },
    // คอลัมน์จัดการ: โผล่เมื่อมีสิทธิ์อย่างน้อย 1 อย่าง (แก้ไข = Head/Manager/Admin, ลบ = Admin ขึ้นไป)
    ...(canEdit || canDelete
      ? [
          {
            title: "จัดการ",
            key: "actions",
            align: "center",
            width: 160,
            render: (_, record) => (
              <Space>
                {canEdit && (
                  <Button size="small" onClick={() => openEdit(record)}>
                    แก้ไข
                  </Button>
                )}
                {canDelete && (
                  <Popconfirm
                    title="ยืนยันการลบรายการนี้?"
                    description="ลบแล้วกู้คืนไม่ได้"
                    onConfirm={() => handleDelete(record.id)}
                    okText="ลบ"
                    okButtonProps={{ danger: true }}
                    cancelText="ยกเลิก"
                  >
                    <Button size="small" danger>
                      ลบ
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-6">
        <Header category="Workspace" title="Blacklist" />
      </div>

      <div className="mb-4 flex gap-3">
        <Input
          placeholder="ค้นหา UserId / ชื่อบัญชี / หัวข้อ / เลขบัญชี"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          className="w-96"
        />
        <Button type="primary" onClick={handleSearch}>
          ค้นหา
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={isLoading || isFetching}
        dataSource={rows}
        columns={columns}
        bordered
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
          showTotal: (t) => `ทั้งหมด ${t} รายการ`,
        }}
      />

      {/* Edit modal — เตรียมไว้ (เปิดได้เมื่อ SHOW_EDIT_DELETE = true) */}
      <Modal
        title="แก้ไข Blacklist"
        open={!!editing}
        onOk={handleEditOk}
        onCancel={() => setEditing(null)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        okButtonProps={{ loading: isUpdating }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="topicId" label="หัวข้อ">
            <Select placeholder="เลือกหัวข้อ">
              {topics.map((t) => (
                <Select.Option key={t.id} value={t.id}>
                  {t.topic}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="accountName" label="ชื่อบัญชี">
            <Input />
          </Form.Item>
          <Form.Item name="bank" label="ธนาคาร">
            <Select showSearch optionFilterProp="children" allowClear placeholder="เลือกธนาคาร">
              {bankList.map((b) => (
                <Select.Option key={b.code} value={b.thai_name}>
                  {b.thai_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="bankAccount" label="เลขบัญชี">
            <Input />
          </Form.Item>
          <Form.Item name="note" label="หมายเหตุ">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BlacklistPage;
